import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template65: Segunda página de Día libre de Tokio (excursiones: Monte Fuji, Nikko, Disneyland)
// Complementa Template61 con opciones de excursiones y Tokyo Disneyland
const Template65 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const [imgErrors, setImgErrors] = useState({
		logo: false,
		fuji: false,
		nikko: false,
		disney: false,
		disneySticker: false,
	});
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const logoError = imgErrors.logo;
	const setLogoError = (v) => setImgErrors((p) => ({ ...p, logo: v }));
	const fujiError = imgErrors.fuji;
	const setFujiError = (v) => setImgErrors((p) => ({ ...p, fuji: v }));
	const nikkoError = imgErrors.nikko;
	const setNikkoError = (v) => setImgErrors((p) => ({ ...p, nikko: v }));
	const disneyError = imgErrors.disney;
	const setDisneyError = (v) => setImgErrors((p) => ({ ...p, disney: v }));
	const disneyStickerError = imgErrors.disneySticker;
	const setDisneyStickerError = (v) =>
		setImgErrors((p) => ({ ...p, disneySticker: v }));

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Misma lógica que Template61: header #e24217 en páginas subsiguientes de la guía Tokio
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const fujiImage = getImageSrc("fuji");
	const nikkoImage = getImageSrc("nikko");
	const disneyImage = getImageSrc("disney");
	const disneyStickerImage = getImageSrc("disney-sticker");

	const introText = page.texts?.[1]?.content;
	const fujiTitle = page.texts?.[2]?.content;
	const fujiDesc = page.texts?.[3]?.content;
	const fujiCajaTexto = page.texts?.[4]?.content;
	const nikkoTitle = page.texts?.[5]?.content;
	const nikkoDesc = page.texts?.[6]?.content;
	const disneyTitle = page.texts?.[7]?.content;
	const disneyDesc = page.texts?.[8]?.content;
	const disneyCajaTexto = page.texts?.[9]?.content;
	const fujiSabiasQue = page.texts?.[10]?.content;
	const disneySabiasQue = page.texts?.[11]?.content;
	const ctaFujiHtml = page.texts?.[12]?.content;
	const ctaNikkoHtml = page.texts?.[13]?.content;
	const ctaDisneyHtml = page.texts?.[14]?.content;

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	const linkStyle = {
		color: "#f28b35",
		fontWeight: 600,
		textDecoration: "underline",
	};

	const ctaBoxStyle = {
		backgroundColor: "#fff8db",
		border: "2px solid #f5c400",
		borderRadius: "12px",
		padding: "10px 12px",
		display: "flex",
		gap: "8px",
		alignItems: "flex-start",
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "12px",
		color: "#333",
	};

	const sabiasQueBoxStyle = {
		backgroundColor: "#fff8db",
		border: "2px solid #f5c400",
		borderRadius: "16px 16px 0px 16px",
		padding: "10px 12px",
		fontSize: 11,
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		color: contentColor,
		boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
		maxWidth: 140,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#ffffff",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				".marginless": {
					marginBottom: "0px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{logoImage && !logoError && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						onError={() => setLogoError(true)}
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* CONTENIDO */}
			<Box
				sx={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					gap: "16px",
					flex: 1,
				}}
			>
				{/* Intro */}
				<Box data-block-id="text-1" component="p" sx={{ ...estiloP, margin: 0 }}>
					{introText}
				</Box>

				{/* MONTE FUJI Y LAGO KAWAGUCHI */}
				<Box data-block-id="text-2" sx={{ position: "relative", display: "flex", gap: 16 }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Box component="h3" sx={estiloH3}>
							{fujiTitle}
						</Box>
						<Box component="p" sx={estiloP}>
							<Box
								sx={{
									position: "relative",
									flexShrink: 0,
									width: "230px",
									float: "right",
									marginTop: "-33px",
									marginRight: "-81px",
								}}
							>
								{fujiImage && !fujiError ? (
									<Box
										component="img"
										src={fujiImage}
										alt="Monte Fuji"
										onError={() => setFujiError(true)}
										sx={{
											width: 110,
											mt: isSmallScreen ? "50px" : "20px",
											ml: "64px",
										}}
									/>
								) : (
									<ImagenNoDisponibleKawaii
										width={110}
										height={110}
										fontFamily={fontFamily || "'Poppins', sans-serif"}
										compact
									/>
								)}
								<Box
									sx={{
										...sabiasQueBoxStyle,
										borderRadius: "16px 16px 0px 16px",
										position: "absolute",
										bottom: isSmallScreen ? "40px" : "40px",
										right: isSmallScreen ? "140px" : "140px",
										maxWidth: 90,
									}}
								>
									{fujiCajaTexto}
								</Box>
							</Box>

							{fujiDesc}
						</Box>
					</Box>
				</Box>
				<Box
					sx={ctaBoxStyle}
					style={{
						position: "relative",
						marginTop: isSmallScreen ? "0px" : "-20px",
					}}
				>
					<Box>
						<Box component="strong">
							🎫 Reserva el tour para ver el Monte Fuji:
						</Box>{" "}
						Puedes reservarlo fácilmente{" "}
						<Box
							component="a"
							href="https://www.getyourguide.com/tokyo-l193/tokyo-mount-fuji-lake-kawaguchi-and-oshino-hakkai-bus-tour-t872268/?partner_id=47M5PHY&utm_medium=online_publisher"
							target="_blank"
							rel="noopener noreferrer"
							sx={{ ...linkStyle, color: "#caa100" }}
						>
							aquí
						</Box>
						.
					</Box>
					<Box
						sx={{
							position: "absolute",
							top: "-30px",
							right: "40%",
						}}
					>
						<SabiasQue icono="🗻" texto={fujiSabiasQue} />
					</Box>
				</Box>

				{/* EXCURSIÓN A NIKKO */}
				<Box data-block-id="text-3" sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
				<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
					{nikkoImage && !nikkoError ? (
						<img
							src={nikkoImage}
							alt="Nikko"
							onError={() => setNikkoError(true)}
							style={{
								width: isSmallScreen ? "40%" : "40%",
								maxWidth: isSmallScreen ? "40%" : "40%",
								borderRadius: "12px",
								objectFit: "cover",
								height: 110,
								flexShrink: 0,
							}}
						/>
					) : (
						<ImagenNoDisponibleKawaii
							width={isSmallScreen ? "40%" : "40%"}
							height={110}
							fontFamily={fontFamily || "'Poppins', sans-serif"}
							compact
						/>
					)}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Box component="h3" sx={estiloH3}>
							{nikkoTitle}
						</Box>
						<Box component="p" sx={estiloP}>
							{nikkoDesc}
						</Box>
					</Box>
				</Box>

				<Box sx={ctaBoxStyle}>
					<Box>
						<Box component="strong">🎫 Excursión en autobús a Nikko:</Box>{" "}
						Puedes contratarla{" "}
						<Box
							component="a"
							href="https://www.getyourguide.com/tokyo-l193/nikko-kegon-falls-lake-chuzenji-toshogu-temple-t862525/?partner_id=47M5PHY&utm_medium=online_publisher"
							target="_blank"
							rel="noopener noreferrer"
							sx={{ ...linkStyle, color: "#caa100" }}
						>
							aquí
						</Box>
						.
					</Box>
				</Box>
				</Box>

				{/* TOKYO DISNEYLAND O DISNEYSEA */}
				<Box data-block-id="text-4" sx={{ position: "relative", display: "flex", gap: "16px" }}>
					{disneyImage && !disneyError ? (
						<img
							src={disneyImage}
							alt="Tokyo Disneyland"
							onError={() => setDisneyError(true)}
							style={{
								width: isSmallScreen ? "40%" : "40%",
								maxWidth: isSmallScreen ? "40%" : "40%",
								borderRadius: "12px",
								objectFit: "cover",
								height: 110,
								flexShrink: 0,
							}}
						/>
					) : (
						<ImagenNoDisponibleKawaii
							width={isSmallScreen ? "40%" : "40%"}
							height={110}
							fontFamily={fontFamily || "'Poppins', sans-serif"}
							compact
						/>
					)}
					<Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
						<Box component="h3" sx={estiloH3}>
							{disneyTitle}
						</Box>
						<Box component="p" sx={estiloP}>
							{disneyDesc}
						</Box>
						<Box sx={ctaBoxStyle} style={{ position: "relative" }}>
							<Box>
								<Box component="strong">🎫 Entradas a Tokyo Disneyland:</Box>{" "}
								Compra{" "}
								<Box
									component="a"
									href="https://www.getyourguide.com/tokyo-l193/tokyo-disneyland-1-day-passport-t459174/?partner_id=47M5PHY&utm_medium=online_publisher"
									target="_blank"
									rel="noopener noreferrer"
									sx={{ ...linkStyle, color: "#caa100" }}
								>
									aquí
								</Box>
								.
							</Box>
							<Box
								sx={{
									position: "absolute",
									bottom: "-32px",
								}}
							>
								<SabiasQue icono="🎢" texto={disneySabiasQue} />
							</Box>
						</Box>
					</Box>
					{disneyStickerImage && !disneyStickerError && (
						<Box
							component="img"
							src={disneyStickerImage}
							alt=""
							onError={() => setDisneyStickerError(true)}
							sx={{
								width: 110,
								position: "absolute",
								bottom: isSmallScreen ? -57 : -80,
								left: -25,
								zIndex: 1,
							}}
						/>
					)}
					<Box
						sx={{
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							marginTop: 1,
							maxWidth: 140,
							position: "absolute",
							bottom: isSmallScreen ? "20px" : "-20px",
							left: isSmallScreen ? "-10px" : "40px",
							zIndex: 1,
						}}
					>
						🚇 {disneyCajaTexto}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template65.thumbnail = "/images/template-1.png";

export default Template65;
